import React, { useEffect, useState, useMemo, useRef, useCallback, ChangeEvent } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import BuildOutlined from "@mui/icons-material/BuildOutlined";
import EditNoteOutlined from "@mui/icons-material/EditNoteOutlined";
import StickyNote2Outlined from "@mui/icons-material/StickyNote2Outlined";
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import EventFilledIcon from "@mui/icons-material/Event";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import EuroOutlinedIcon from "@mui/icons-material/EuroOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { ChevronRight } from "@mui/icons-material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import Moment from "react-moment";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  ChangeSolvingServiceReport,
  ServiceReportFile,
  ServiceReportFileTypeCode,
  ServiceReportStatusCode,
  ServiceReportsState,
} from "../models/ServiceReportsModel";
import { CustomerMachineState } from "../models/models";
import { CurrencyCode } from "../../../common/models/CommonModels";
import {
  createSolvingServiceReport,
  changeSolvingServiceReport,
  getMachineParts,
  getServiceReport,
  getServiceReports,
} from "../customerMachineServiceReportsSlice";
import { getMachine } from "../customerMachinerySlice";
import { dt } from "../utils/dt";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import useTranslation from "../../../shared/hooks/useTranslation";
import { getMachineDisplayName } from "../../../shared/utils/machineUtils";
import WizardStepper from "../../serviceOffers/components/WizardStepper";
import Loader from "../../../shared/components/Loader";

// ═══════════════════════════════════════════════════
//  FILE HELPERS
// ═══════════════════════════════════════════════════

const getFileIcon = (name: string) => {
  const ext = name.toLowerCase().split(".").pop();
  if (ext === "pdf") return <PictureAsPdfOutlinedIcon sx={{ fontSize: 18, color: "#E74C3C" }} />;
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext || ""))
    return <ImageOutlinedIcon sx={{ fontSize: 18, color: dt.blue }} />;
  return <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: dt.textSecondary }} />;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const MAX_FILE_SIZE_MB = 10;

// ═══════════════════════════════════════════════════
//  STYLES
// ═══════════════════════════════════════════════════

const sectionSx = {
  backgroundColor: dt.cardBg,
  borderRadius: dt.radius,
  border: `1px solid ${dt.cardBorder}`,
  boxShadow: dt.cardShadow,
  p: { xs: 2.5, md: 4 },
  display: "flex",
  flexDirection: "column" as const,
  gap: 3,
};

const sectionHeaderSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  mb: 1,
};

const sectionLabelSx = {
  fontFamily: dt.font,
  fontSize: "12px",
  fontWeight: 700,
  color: dt.textSecondary,
  textTransform: "uppercase" as const,
  letterSpacing: "0.5px",
};

const sectionIconSx = {
  fontSize: 16,
  color: dt.textMuted,
};

const labelSx = {
  fontFamily: dt.font,
  fontSize: "13px",
  fontWeight: 600,
  color: dt.textPrimary,
  letterSpacing: "0.1px",
  mb: 0.5,
};

const readOnlyValueSx = {
  fontFamily: dt.font,
  fontSize: "14px",
  fontWeight: 500,
  color: dt.textPrimary,
  lineHeight: 1.6,
};

// ═══════════════════════════════════════════════════
//  COMPONENT
// ═══════════════════════════════════════════════════

const SolveServiceReportPage = () => {
  useRedesignSetup();

  const { id: machineId, reportNumber } = useParams<{ id: string; reportNumber: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isPhone = useMediaQuery("(max-width:768px)");

  const { translate } = useTranslation(["myMachinery", "servicing", "common"]);

  const { machine } = useAppSelector<CustomerMachineState>((state: any) => state.customerMachinery);
  const { serviceReport, serviceReportsQuery, machineParts, loading } = useAppSelector<ServiceReportsState>(
    (state: any) => state.customerServiceReports
  );

  const isStatusCodeInProgress = serviceReport?.statusCode === ServiceReportStatusCode.inProgress;

  const [activeStep, setActiveStep] = useState(0);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [files, setFiles] = useState<ServiceReportFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileError, setFileError] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const stepLabels = useMemo(
    () => [
      translate("myMachinery:srPage.stepReportDetails"),
      translate("myMachinery:srPage.stepSolution"),
    ],
    [translate]
  );

  const {
    register,
    watch,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangeSolvingServiceReport>();

  const closeDate = watch("closeDate");

  // ── Load data ──────────────────────────────────────────
  useEffect(() => {
    if (machineId) {
      dispatch(getMachine(machineId));
    }
    if (reportNumber) {
      dispatch(getServiceReport(reportNumber));
    }
    dispatch(getMachineParts());
  }, [machineId, reportNumber, dispatch]);

  // ── Pre-fill form ──────────────────────────────────────
  useEffect(() => {
    if (serviceReport) {
      const solvedFiles = serviceReport.files?.filter(
        (x) => x.typeCode === ServiceReportFileTypeCode.solvingReportDocument
      ) || [];
      reset({
        solvingDescription: serviceReport.solvingDescription || "",
        closeDate: serviceReport.closeDate || undefined,
        cost: serviceReport.cost || {},
        files: serviceReport.files || null,
      });
      setFiles(solvedFiles);
    }
  }, [serviceReport, reset]);

  const displayName = machine ? getMachineDisplayName(machine) : "";

  // ── Part name lookup from machine parts ───────────────
  const categoryName = useMemo(() => {
    if (!serviceReport?.affectedPartCategoryId || !machineParts) return undefined;
    for (const group of machineParts) {
      const part = group.parts?.find((p) => p.id === serviceReport.affectedPartCategoryId);
      if (part) return part.name;
    }
    return undefined;
  }, [machineParts, serviceReport?.affectedPartCategoryId]);

  // ── Report files (for read-only step) ──────────────────
  const reportFiles = useMemo(
    () => serviceReport?.files?.filter((f) => f.typeCode === ServiceReportFileTypeCode.reportDocument) || [],
    [serviceReport?.files]
  );

  // ── File upload handlers ───────────────────────────────
  const readFileAsDataUrl = useCallback(
    (file: File): Promise<ServiceReportFile | null> => {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setFileError(translate("common:uploadFile.fileIsTooBig"));
        return Promise.resolve(null);
      }
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          resolve({ name: file.name, content: reader.result as string, size: file.size, typeCode: ServiceReportFileTypeCode.solvingReportDocument });
        };
        reader.onerror = () => resolve(null);
      });
    },
    [translate]
  );

  const processFiles = useCallback(
    async (fileList: File[]) => {
      setFileError(undefined);
      const results = await Promise.all(fileList.map((f) => readFileAsDataUrl(f)));
      const validFiles = results.filter((f): f is ServiceReportFile => f !== null);
      if (validFiles.length > 0) {
        setFiles((prev) => [...prev, ...validFiles]);
      }
    },
    [readFileAsDataUrl]
  );

  const handleFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        processFiles(Array.from(e.dataTransfer.files));
      }
    },
    [processFiles]
  );

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(Array.from(e.target.files));
      }
      e.target.value = "";
    },
    [processFiles]
  );

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // ── Step navigation ────────────────────────────────────
  const handleNext = () => setActiveStep(1);
  const handleBack = () => setActiveStep(0);
  const handleStepClick = (step: number) => {
    if (step < activeStep) setActiveStep(step);
  };

  // ── Submit ─────────────────────────────────────────────
  const onSubmit: SubmitHandler<ChangeSolvingServiceReport> = async (data) => {
    data.closeDate = data.closeDate || moment().toDate();
    data.files = files;
    data.cost = { id: CurrencyCode.eur, value: Number(data.cost?.value || 0) };

    const srNumber = serviceReport?.number || reportNumber;
    if (isStatusCodeInProgress) {
      await dispatch(createSolvingServiceReport({ model: data, serviceReportNumber: srNumber }));
    } else {
      await dispatch(changeSolvingServiceReport({ model: data, serviceReportNumber: srNumber }));
    }
    if (machineId) {
      await dispatch(getServiceReports({ machineId, query: serviceReportsQuery }));
    }
    navigate(`/machinery/${machineId}`);
  };

  // ── Loading ────────────────────────────────────────────
  if ((loading && !serviceReport) || (!machine && machineId)) {
    return <Loader />;
  }

  if (!serviceReport) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "400px", fontFamily: dt.font }}>
        <Typography sx={{ color: dt.textSecondary }}>{translate("myMachinery:srDetail.notFound")}</Typography>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100%",
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
        background: dt.pageBg,
        backgroundImage: `radial-gradient(circle, ${dt.pageDotColor} 1px, transparent 1px)`,
        backgroundSize: "20px 20px",
      }}
    >
      {/* ═══ Header bar with stepper ═══ */}
      <Box
        sx={{
          background: "#FFFFFF",
          borderBottom: `1px solid ${dt.cardBorder}`,
          px: isPhone ? 2 : 4,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          animation: "headerReveal 0.5s cubic-bezier(0.4,0,0.2,1) both",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={() => navigate(`/machinery/${machineId}`)}
            sx={{ color: dt.textSecondary, "&:hover": { color: dt.blue, backgroundColor: dt.blueSoft } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Box sx={{ fontSize: { xs: "16px", md: "18px" }, fontWeight: 700, color: dt.textPrimary }}>
              {translate("myMachinery:srPage.solveReportTitle")}
            </Box>
            {/* Breadcrumb */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mt: 0.25 }}>
              <Box
                onClick={() => navigate("/machinery")}
                sx={{ fontSize: "12px", fontWeight: 500, color: dt.blue, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
              >
                {translate("myMachinery:breadcrumbEquipment")}
              </Box>
              <ChevronRight sx={{ fontSize: 14, color: dt.textMuted }} />
              <Box
                onClick={() => navigate(`/machinery/${machineId}`)}
                sx={{
                  fontSize: "12px", fontWeight: 500, color: dt.blue, cursor: "pointer",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "120px",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {displayName}
              </Box>
              <ChevronRight sx={{ fontSize: 14, color: dt.textMuted }} />
              <Box sx={{ fontSize: "12px", fontWeight: 500, color: dt.textMuted, fontFamily: dt.fontMono }}>
                #{reportNumber}
              </Box>
            </Box>
          </Box>
        </Box>

        <WizardStepper steps={stepLabels} activeStep={activeStep} onStepClick={handleStepClick} />
      </Box>

      {/* ═══ Content area ═══ */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          px: isPhone ? 2 : 4,
          py: 3,
          pb: 12,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: "720px" }}>
          {/* ── Step 1: Read-only Report Details ── */}
          {activeStep === 0 && (
            <Box sx={{ animation: "fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) both" }}>
              {/* Hint text */}
              <Box sx={{ mb: 2, fontFamily: dt.font, fontSize: "13px", color: dt.textSecondary, fontStyle: "italic" }}>
                {translate("myMachinery:srPage.reportDetailsReadOnly")}
              </Box>

              <Box sx={sectionSx}>
                {/* Title */}
                <Box>
                  <Box sx={sectionHeaderSx}>
                    <DescriptionOutlined sx={sectionIconSx} />
                    <Box sx={sectionLabelSx}>{translate("myMachinery:reportTitleSection")}</Box>
                  </Box>
                  <Box sx={readOnlyValueSx}>{serviceReport.title}</Box>
                </Box>

                {/* Date & Time */}
                <Box>
                  <Box sx={sectionHeaderSx}>
                    <CalendarTodayOutlined sx={sectionIconSx} />
                    <Box sx={sectionLabelSx}>{translate("myMachinery:dateAndTimeSection")}</Box>
                  </Box>
                  <Box sx={readOnlyValueSx}>
                    {serviceReport.openDate ? (
                      <Moment date={serviceReport.openDate} format="DD.MM.YYYY, HH:mm" />
                    ) : (
                      "-"
                    )}
                  </Box>
                </Box>

                {/* Affected Part */}
                <Box>
                  <Box sx={sectionHeaderSx}>
                    <BuildOutlined sx={sectionIconSx} />
                    <Box sx={sectionLabelSx}>{translate("myMachinery:affectedPartSection")}</Box>
                  </Box>
                  <Box sx={readOnlyValueSx}>
                    {categoryName || translate("myMachinery:srPage.noPartSpecified")}
                  </Box>
                </Box>

                {/* Description */}
                <Box>
                  <Box sx={sectionHeaderSx}>
                    <EditNoteOutlined sx={sectionIconSx} />
                    <Box sx={sectionLabelSx}>{translate("myMachinery:descriptionSection")}</Box>
                  </Box>
                  <Box
                    sx={{
                      ...readOnlyValueSx,
                      whiteSpace: "pre-wrap",
                      color: serviceReport.description ? dt.textSecondary : dt.textMuted,
                      fontStyle: serviceReport.description ? "normal" : "italic",
                    }}
                  >
                    {serviceReport.description || translate("myMachinery:srPage.noDescription")}
                  </Box>
                </Box>

                {/* Technical Notes (read-only) */}
                {serviceReport.notes && (
                  <Box>
                    <Box sx={sectionHeaderSx}>
                      <StickyNote2Outlined sx={sectionIconSx} />
                      <Box sx={sectionLabelSx}>{translate("myMachinery:technicalNotesSection")}</Box>
                    </Box>
                    <Box
                      sx={{
                        ...readOnlyValueSx,
                        whiteSpace: "pre-wrap",
                        color: dt.textSecondary,
                      }}
                    >
                      {serviceReport.notes}
                    </Box>
                  </Box>
                )}

                {/* Attachments */}
                {reportFiles.length > 0 && (
                  <Box>
                    <Box sx={sectionHeaderSx}>
                      <AttachFileOutlined sx={sectionIconSx} />
                      <Box sx={sectionLabelSx}>{translate("myMachinery:attachmentsSection")}</Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                      {reportFiles.map((file, index) => (
                        <Box
                          key={`${file.name}-${index}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1,
                            borderRadius: dt.radiusSm,
                            border: `1px solid ${dt.cardBorder}`,
                            backgroundColor: "#FAFBFC",
                          }}
                        >
                          {getFileIcon(file.name)}
                          <Typography
                            sx={{
                              fontFamily: dt.font, fontSize: "13px", fontWeight: 500, color: dt.textPrimary,
                              flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
                            }}
                          >
                            {file.name}
                          </Typography>
                          {file.size > 0 && (
                            <Typography sx={{ fontFamily: dt.fontMono, fontSize: "11px", fontWeight: 500, color: dt.textMuted, flexShrink: 0 }}>
                              {formatFileSize(file.size)}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Next button */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  variant="contained"
                  endIcon={<NavigateNextIcon />}
                  onClick={handleNext}
                  sx={{
                    background: dt.serviceReports.createBtnGradient,
                    borderRadius: dt.radiusPill,
                    fontFamily: dt.font, fontWeight: 600, textTransform: "none",
                    px: 3, py: 1,
                    boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
                    "&:hover": { background: `linear-gradient(135deg, #2E86C1 0%, ${dt.blue} 100%)` },
                  }}
                >
                  {translate("myMachinery:srPage.next")}
                </Button>
              </Box>
            </Box>
          )}

          {/* ── Step 2: Solving Form ── */}
          {activeStep === 1 && (
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              sx={{ animation: "fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) both" }}
            >
              <Box sx={sectionSx}>
                {/* Green accent bar at top */}
                <Box sx={{ height: "4px", background: `linear-gradient(90deg, ${dt.serviceReports.dialogGreenAccent}, #2ECC71)`, mx: { xs: -2.5, md: -4 }, mt: { xs: -2.5, md: -4 }, borderRadius: `${dt.radius} ${dt.radius} 0 0` }} />

                {/* ── Close Date & Time ── */}
                <Box>
                  <Box sx={sectionHeaderSx}>
                    <CalendarTodayOutlined sx={sectionIconSx} />
                    <Box sx={sectionLabelSx}>{translate("myMachinery:srPage.closeDateSection")}</Box>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr", gap: 2 }}>
                    <Box>
                      <InputLabel sx={labelSx}>{translate("myMachinery:closeDate")}</InputLabel>
                      <FormControl fullWidth size="small">
                        <Controller
                          control={control}
                          name="closeDate"
                          rules={{
                            validate: (value) => {
                              if (value && serviceReport?.openDate) {
                                if (moment(value) > moment()) {
                                  return translate("myMachinery:closeDateFuture");
                                }
                                return (
                                  moment(value) >= moment(serviceReport.openDate) ||
                                  translate("myMachinery:mustBeGreaterThan", {
                                    date: moment(serviceReport.openDate).format("DD.MM.YYYY, HH:mm"),
                                  })
                                );
                              }
                            },
                          }}
                          render={({ field }) => (
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                              <DatePicker
                                {...field}
                                value={moment(field.value)}
                                onChange={(value) => field.onChange(value?.toDate())}
                                format="DD.MM.YYYY"
                                minDate={moment(serviceReport?.openDate)}
                                maxDate={moment()}
                                disableFuture
                                views={["year", "month", "day"]}
                                open={isCalendarOpen}
                                onOpen={() => setIsCalendarOpen(true)}
                                onClose={() => setIsCalendarOpen(false)}
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    InputProps: {
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <IconButton
                                            onClick={() => setIsCalendarOpen(true)}
                                            sx={{ color: dt.serviceReports.dialogGreenAccent, "&:hover": { backgroundColor: "rgba(39,174,96,0.06)" } }}
                                          >
                                            <EventFilledIcon />
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                    },
                                  },
                                  popper: {
                                    sx: {
                                      "& .MuiPickersDay-root.Mui-selected": {
                                        backgroundColor: dt.serviceReports.dialogGreenAccent,
                                        "&:hover": { backgroundColor: dt.serviceReports.dialogGreenAccent },
                                      },
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          )}
                        />
                      </FormControl>
                    </Box>
                    <Box>
                      <InputLabel sx={labelSx}>{translate("common:common.time")}</InputLabel>
                      <FormControl fullWidth size="small">
                        <Controller
                          control={control}
                          name="closeDate"
                          render={({ field }) => (
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                              <TimeField
                                {...field}
                                value={closeDate ? moment(closeDate) : null}
                                onChange={(value) => {
                                  if (value) {
                                    const currentDate = closeDate ? moment(closeDate) : moment();
                                    const updatedDate = currentDate
                                      .hour(value.hour())
                                      .minute(value.minute())
                                      .second(0)
                                      .millisecond(0);
                                    field.onChange(updatedDate.toDate());
                                  }
                                }}
                                format="HH:mm"
                              />
                            </LocalizationProvider>
                          )}
                        />
                      </FormControl>
                    </Box>
                  </Box>
                  <FormHelperText error>{errors.closeDate?.message}</FormHelperText>
                </Box>

                {/* ── Solving Description ── */}
                <Box>
                  <Box sx={sectionHeaderSx}>
                    <EditNoteOutlined sx={sectionIconSx} />
                    <Box sx={sectionLabelSx}>{translate("myMachinery:srPage.solvingDescriptionSection")}</Box>
                  </Box>
                  <Box
                    sx={{
                      background: dt.inputBg,
                      borderRadius: dt.radiusSm,
                      border: "1.5px solid transparent",
                      padding: "12px",
                      transition: "all 0.2s ease",
                      "&:focus-within": {
                        border: `1.5px solid ${dt.serviceReports.dialogGreenAccent}`,
                        boxShadow: `0 0 0 3px rgba(39,174,96,0.12)`,
                      },
                    }}
                  >
                    <textarea
                      {...register("solvingDescription")}
                      placeholder={translate("myMachinery:srPage.solvingDescriptionPlaceholder")}
                      maxLength={2000}
                      rows={6}
                      style={{
                        border: "none",
                        outline: "none",
                        width: "100%",
                        fontSize: "14px",
                        fontFamily: "var(--font-sans)",
                        color: "#1a1a2e",
                        background: "transparent",
                        resize: "vertical",
                        minHeight: "120px",
                        boxSizing: "border-box",
                      }}
                    />
                  </Box>
                  {errors.solvingDescription && (
                    <FormHelperText error>{errors.solvingDescription?.message}</FormHelperText>
                  )}
                </Box>

                {/* ── Cost ── */}
                <Box>
                  <Box sx={sectionHeaderSx}>
                    <EuroOutlinedIcon sx={sectionIconSx} />
                    <Box sx={sectionLabelSx}>{translate("myMachinery:srPage.costSection")}</Box>
                  </Box>
                  <TextField
                    type="number"
                    fullWidth
                    size="small"
                    variant="outlined"
                    {...register("cost.value", {
                      required: translate("common:forms.required"),
                      max: { value: 100000, message: translate("myMachinery:maxValue100k") },
                      min: { value: 0, message: translate("myMachinery:minValue0") },
                    })}
                    error={!!errors.cost?.value}
                    helperText={errors.cost?.value?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box sx={{ fontFamily: dt.fontMono, fontWeight: 600, color: dt.textSecondary, fontSize: "14px" }}>
                            &euro;
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputBase-input": { fontFamily: dt.fontMono },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: dt.radiusSm,
                        "&.Mui-focused fieldset": { borderColor: dt.serviceReports.dialogGreenAccent },
                      },
                    }}
                  />
                  <input type="hidden" {...register("cost.id", { value: CurrencyCode.eur })} />
                </Box>

                {/* ── Solution Attachments ── */}
                <Box>
                  <Box sx={sectionHeaderSx}>
                    <AttachFileOutlined sx={sectionIconSx} />
                    <Box sx={sectionLabelSx}>{translate("myMachinery:srPage.solvingAttachmentsSection")}</Box>
                  </Box>

                  <Box
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
                    onDrop={handleFileDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      height: "100px",
                      border: `2px dashed ${fileError ? "#C0392B" : isDragOver ? dt.serviceReports.dialogGreenAccent : dt.cardBorder}`,
                      borderRadius: dt.radiusSm,
                      backgroundColor: isDragOver ? "rgba(39,174,96,0.06)" : dt.inputBg,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": { borderColor: dt.serviceReports.dialogGreenAccent, backgroundColor: "rgba(39,174,96,0.04)" },
                    }}
                  >
                    <CloudUploadOutlinedIcon
                      sx={{ fontSize: 28, color: isDragOver ? dt.serviceReports.dialogGreenAccent : dt.textMuted, transition: "color 0.2s ease" }}
                    />
                    <Typography sx={{ fontSize: "13px", color: dt.textSecondary, fontFamily: dt.font }}>
                      {translate("myMachinery:dropFilesHere")}
                    </Typography>
                    <Box sx={{ fontSize: "11px", color: dt.textMuted, fontFamily: dt.font }}>
                      {translate("myMachinery:supportedFormatsHint")}
                    </Box>
                  </Box>

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleFileInputChange}
                  />

                  {fileError && <FormHelperText error sx={{ mt: 0.5 }}>{fileError}</FormHelperText>}

                  {files.length > 0 && (
                    <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
                      {files.map((file, index) => (
                        <Box
                          key={`${file.name}-${index}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1,
                            borderRadius: dt.radiusSm,
                            border: `1px solid ${dt.cardBorder}`,
                            backgroundColor: dt.cardBg,
                            transition: "all 0.2s ease",
                            "&:hover": { backgroundColor: "#FAFAFA", "& .file-delete": { opacity: 1 } },
                            animation: `fadeUp 0.3s ease ${index * 0.05}s both`,
                          }}
                        >
                          {getFileIcon(file.name)}
                          <Typography
                            sx={{
                              fontFamily: dt.font, fontSize: "13px", fontWeight: 500, color: dt.textPrimary,
                              flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
                            }}
                          >
                            {file.name}
                          </Typography>
                          {file.size > 0 && (
                            <Typography sx={{ fontFamily: dt.fontMono, fontSize: "11px", fontWeight: 500, color: dt.textMuted, flexShrink: 0 }}>
                              {formatFileSize(file.size)}
                            </Typography>
                          )}
                          <Tooltip title={translate("common:buttons.delete")}>
                            <IconButton
                              className="file-delete"
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleRemoveFile(index); }}
                              sx={{
                                opacity: 0.5, transition: "opacity 0.2s ease", p: "4px",
                                "&:hover": { backgroundColor: "rgba(220,38,38,0.08)", color: "#C0392B" },
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Bottom action row */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
                <Button
                  variant="text"
                  startIcon={<NavigateBeforeIcon />}
                  onClick={handleBack}
                  sx={{
                    fontFamily: dt.font, fontWeight: 600, fontSize: "14px", textTransform: "none",
                    color: dt.textSecondary, borderRadius: dt.radiusSm, px: 2, py: 1,
                    "&:hover": { backgroundColor: dt.inputBg, color: dt.textPrimary },
                  }}
                >
                  {translate("myMachinery:srPage.back")}
                </Button>

                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Button
                    variant="text"
                    onClick={() => navigate(`/machinery/${machineId}`)}
                    sx={{
                      fontFamily: dt.font, fontWeight: 600, fontSize: "14px", textTransform: "none",
                      color: dt.textSecondary, borderRadius: dt.radiusSm, px: 2.5, py: 1,
                      "&:hover": { backgroundColor: dt.inputBg, color: dt.textPrimary },
                    }}
                  >
                    {translate("myMachinery:srPage.cancel")}
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<CheckCircleOutlineIcon sx={{ fontSize: "18px !important" }} />}
                    sx={{
                      background: `linear-gradient(135deg, ${dt.serviceReports.dialogGreenAccent} 0%, #2ECC71 100%)`,
                      borderRadius: dt.radiusPill,
                      fontFamily: dt.font, fontWeight: 600, textTransform: "none",
                      px: 3, py: 1,
                      boxShadow: "0 2px 8px rgba(39,174,96,0.25)",
                      "&:hover": {
                        background: `linear-gradient(135deg, #229954 0%, ${dt.serviceReports.dialogGreenAccent} 100%)`,
                        boxShadow: "0 4px 14px rgba(39,174,96,0.35)",
                      },
                    }}
                  >
                    {translate("myMachinery:srPage.saveAsSolved")}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SolveServiceReportPage;
